const VERSION = 5;
const SIZE = VERSION * 4 + 17;
const DATA_CODEWORDS = 108;
const ERROR_CODEWORDS = 26;

function multiply(x: number, y: number) {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z;
}

function divisor(degree: number) {
  const result = new Uint8Array(degree);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < degree; j++) {
      result[j] = multiply(result[j], root);
      if (j + 1 < degree) result[j] ^= result[j + 1];
    }
    root = multiply(root, 2);
  }
  return result;
}

function remainder(data: Uint8Array, degree: number) {
  const result = new Uint8Array(degree);
  const polynomial = divisor(degree);
  for (const byte of data) {
    const factor = byte ^ result[0];
    result.copyWithin(0, 1);
    result[result.length - 1] = 0;
    for (let i = 0; i < result.length; i++)
      result[i] ^= multiply(polynomial[i], factor);
  }
  return result;
}

function appendBits(bits: number[], value: number, length: number) {
  for (let i = length - 1; i >= 0; i--) bits.push((value >>> i) & 1);
}

function codewords(value: string) {
  const bytes = new TextEncoder().encode(value);
  if (bytes.length > 106)
    throw new Error("Verification URL is too long for the embedded QR code.");
  const bits: number[] = [];
  appendBits(bits, 0x4, 4);
  appendBits(bits, bytes.length, 8);
  for (const byte of bytes) appendBits(bits, byte, 8);
  const capacity = DATA_CODEWORDS * 8;
  appendBits(bits, 0, Math.min(4, capacity - bits.length));
  while (bits.length % 8) bits.push(0);
  for (let pad = 0xec; bits.length < capacity; pad ^= 0xec ^ 0x11)
    appendBits(bits, pad, 8);
  const data = new Uint8Array(DATA_CODEWORDS);
  for (let i = 0; i < data.length; i++) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | bits[i * 8 + j];
    data[i] = byte;
  }
  return new Uint8Array([...data, ...remainder(data, ERROR_CODEWORDS)]);
}

function formatBits(mask: number) {
  const data = (1 << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  return ((data << 10) | rem) ^ 0x5412;
}

function matrix(value: string) {
  const modules = Array.from({ length: SIZE }, () =>
    Array<boolean>(SIZE).fill(false),
  );
  const functionModule = Array.from({ length: SIZE }, () =>
    Array<boolean>(SIZE).fill(false),
  );
  const setFunction = (x: number, y: number, dark: boolean) => {
    if (x >= 0 && y >= 0 && x < SIZE && y < SIZE) {
      modules[y][x] = dark;
      functionModule[y][x] = true;
    }
  };
  const finder = (cx: number, cy: number) => {
    for (let dy = -4; dy <= 4; dy++)
      for (let dx = -4; dx <= 4; dx++) {
        const distance = Math.max(Math.abs(dx), Math.abs(dy));
        setFunction(cx + dx, cy + dy, distance !== 2 && distance !== 4);
      }
  };
  const alignment = (cx: number, cy: number) => {
    for (let dy = -2; dy <= 2; dy++)
      for (let dx = -2; dx <= 2; dx++)
        setFunction(
          cx + dx,
          cy + dy,
          Math.max(Math.abs(dx), Math.abs(dy)) !== 1,
        );
  };

  for (let i = 0; i < SIZE; i++) {
    setFunction(6, i, i % 2 === 0);
    setFunction(i, 6, i % 2 === 0);
  }
  finder(3, 3);
  finder(SIZE - 4, 3);
  finder(3, SIZE - 4);
  alignment(30, 30);

  const drawFormat = (mask: number) => {
    const bits = formatBits(mask);
    const bit = (i: number) => ((bits >>> i) & 1) !== 0;
    for (let i = 0; i <= 5; i++) setFunction(8, i, bit(i));
    setFunction(8, 7, bit(6));
    setFunction(8, 8, bit(7));
    setFunction(7, 8, bit(8));
    for (let i = 9; i < 15; i++) setFunction(14 - i, 8, bit(i));
    for (let i = 0; i < 8; i++) setFunction(SIZE - 1 - i, 8, bit(i));
    for (let i = 8; i < 15; i++)
      setFunction(8, SIZE - 15 + i, bit(i));
    setFunction(8, SIZE - 8, true);
  };
  drawFormat(0);

  const data = codewords(value);
  let bitIndex = 0;
  let upward = true;
  for (let right = SIZE - 1; right >= 1; right -= 2) {
    if (right === 6) right--;
    for (let vertical = 0; vertical < SIZE; vertical++) {
      const y = upward ? SIZE - 1 - vertical : vertical;
      for (let column = 0; column < 2; column++) {
        const x = right - column;
        if (functionModule[y][x]) continue;
        const raw =
          bitIndex < data.length * 8
            ? ((data[bitIndex >>> 3] >>> (7 - (bitIndex & 7))) & 1) !== 0
            : false;
        modules[y][x] = raw !== ((x + y) % 2 === 0);
        bitIndex++;
      }
    }
    upward = !upward;
  }
  return modules;
}

export function qrSvg(value: string, scale = 4, border = 4) {
  const modules = matrix(value);
  const dimension = (SIZE + border * 2) * scale;
  const path: string[] = [];
  for (let y = 0; y < SIZE; y++)
    for (let x = 0; x < SIZE; x++)
      if (modules[y][x])
        path.push(`M${(x + border) * scale},${(y + border) * scale}h${scale}v${scale}h-${scale}z`);
  return `<svg xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Document verification QR code" viewBox="0 0 ${dimension} ${dimension}" shape-rendering="crispEdges"><rect width="100%" height="100%" fill="#fff"/><path d="${path.join("")}" fill="#102733"/></svg>`;
}
